import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Apply", href: "#apply" },
  { label: "Sponsors", href: "#sponsors" },
  { label: "Impact", href: "#impact" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ showAnnouncement = true }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (href) => {
    setOpen(false);
    if (onLanding) {
      const id = href.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/" + href);
    }
  };

  return (
    <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50">
      {/* Top announcement strip */}
      {showAnnouncement && (
        <div className="bg-blue-600 text-white text-[13.5px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-10 flex items-center justify-center gap-3">
            <span className="font-medium">Run by DH Website Services</span>
            <a
              href="https://dhwebsiteservices.co.uk"
              target="_blank"
              rel="noreferrer"
              data-testid="announce-cta"
              className="bg-white text-blue-700 hover:bg-blue-50 transition-colors rounded-full px-3.5 py-1 font-semibold text-[12.5px] inline-flex items-center gap-1"
            >
              Visit the main site →
            </a>
          </div>
        </div>
      )}

      <div
        className={`transition-all duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-100" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="navbar-logo">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-slate-950 overflow-hidden">
              <img src="/brand/dh-logo.png" alt="DH Web Services" className="w-9 h-9 object-contain" />
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-bold text-[14px] tracking-tight text-slate-900">DH Web Services</span>
              <span className="text-[11px] text-slate-500 tracking-wide">Free Website Initiative</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => goSection(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[14px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2.5">
            <Link
              to="/apply"
              data-testid="nav-apply-btn"
              className="text-[13.5px] font-semibold rounded-full px-5 py-2.5 border border-slate-300 text-slate-900 hover:bg-slate-50 transition-all"
            >
              Apply for free
            </Link>
            <Link
              to="/donate"
              data-testid="nav-donate-btn"
              className="text-[13.5px] font-semibold rounded-full px-5 py-2.5 bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-sm"
            >
              Donate
            </Link>
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden bg-white border-t border-slate-100">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => goSection(l.href)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left text-base font-medium text-slate-800"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-4 flex flex-col gap-3 border-t border-slate-100">
                <Link to="/apply" data-testid="nav-mobile-apply" className="btn-outline-light justify-center">
                  Apply for free
                </Link>
                <Link to="/donate" data-testid="nav-mobile-donate" className="btn-primary justify-center">
                  Donate
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
